import { useEffect, useState } from "react"
import { GetApp } from "../../App"
import { BlockRow, BlockField } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { InfoDialog } from "../../Components/InfoDialog"
import { ILightsReminderLead, ILogLighting, ISailingLightsMode, IShipSettings } from "../../Types"

interface LightsReminderSettingViewProps {
  settings: IShipSettings;
  onEdited: () => void;
}

// Navigation-lights ("Lichterführung") reminder section of the settings tab. The
// master toggle enables the quiet in-app advice card; the nested selects configure
// which sailing lights the ship carries, whether dusk/dawn uses the civil or the
// official (sunset/sunrise) lead, and whether the motoring cone is reminded about.
// The nested inputs stay mounted (hidden when disabled) so EditGroup always
// serialises the `lightsReminderSettings` object consistently.
export function LightsReminderSettingView({ settings, onEdited }: LightsReminderSettingViewProps) {
  const app = GetApp()!;
  const t = app.getT("ShipSettingsView");
  const reminderSettings = settings?.lightsReminderSettings;

  const [enabled, setEnabled] = useState<boolean>(settings?.lightsReminder ?? false);
  const [sailingLights, setSailingLights] = useState<ISailingLightsMode>(reminderSettings?.sailingLights ?? ISailingLightsMode.runningLights);
  const [lead, setLead] = useState<ILightsReminderLead>(reminderSettings?.lead ?? ILightsReminderLead.civil);
  // The motoring cone is optional in many jurisdictions, so it is opted out by
  // default (present in disabledSignals). The checkbox is the inverse: ticked means
  // "remind me", which removes the cone from disabledSignals.
  const [remindMotoringCone, setRemindMotoringCone] = useState<boolean>(!(reminderSettings?.disabledSignals ?? [ILogLighting.motoringCone]).includes(ILogLighting.motoringCone));
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    setEnabled(settings?.lightsReminder ?? false);
  }, [settings?.lightsReminder]);
  useEffect(() => {
    setSailingLights(reminderSettings?.sailingLights ?? ISailingLightsMode.runningLights);
    setLead(reminderSettings?.lead ?? ILightsReminderLead.civil);
    setRemindMotoringCone(!(reminderSettings?.disabledSignals ?? [ILogLighting.motoringCone]).includes(ILogLighting.motoringCone));
  }, [reminderSettings?.sailingLights, reminderSettings?.lead, reminderSettings?.disabledSignals]);

  const disabledSignals = remindMotoringCone ? [] : [ILogLighting.motoringCone];

  return (
    <>
      <BlockRow>
        <h3 className="block-row-followed-by-block-row">{t("lights_title")}</h3>
      </BlockRow>
      <BlockRow className="block-row-checkboxes block-row-one-row">
        <BlockField label={t("lights_enable")} labelAfter={true} labelFor="LightsReminder" className="checkbox-text-bigger">
          <input type="hidden" name="lightsReminder" value={enabled ? "true" : "false"} />
          <input id="LightsReminder" type="checkbox" checked={enabled} onChange={() => { setEnabled(!enabled); onEdited(); }} />
        </BlockField>
        <BlockField>
          <button type="button" className="button" onClick={() => setShowInfo(true)}>
            <span className="question-circle">?</span>  {t("more_info")}
          </button>
        </BlockField>
      </BlockRow>

      {/* Keep the nested inputs mounted so the lightsReminderSettings object is always
        serialised; only hide the configuration rows when the reminder is off. */}
      <input type="hidden" name="lightsReminderSettings.sailingLights" value={sailingLights} />
      <input type="hidden" name="lightsReminderSettings.lead" value={lead} />
      <input type="hidden" name="lightsReminderSettings.disabledSignals.length" value={disabledSignals.length} />
      {disabledSignals.map((signal, i) => (
        <input key={`disabled-${i}`} type="hidden" name={`lightsReminderSettings.disabledSignals[${i}]`} value={signal} />
      ))}

      {enabled && (
        <>
          <BlockRow className="block-row-one-row">
            <BlockField label={t("lights_sailing")} className="block_field_26">
              <select value={sailingLights} onChange={(e) => { setSailingLights(parseInt(e.target.value)); onEdited(); }}>
                <option value={ISailingLightsMode.runningLights}>{t("lights_sailing_runningLights")}</option>
                <option value={ISailingLightsMode.triColor}>{t("lights_sailing_triColor")}</option>
              </select>
            </BlockField>
            <BlockField label={t("lights_lead")} className="block_field_26">
              <select value={lead} onChange={(e) => { setLead(parseInt(e.target.value)); onEdited(); }}>
                <option value={ILightsReminderLead.civil}>{t("lights_lead_civil")}</option>
                <option value={ILightsReminderLead.official}>{t("lights_lead_official")}</option>
              </select>
            </BlockField>
          </BlockRow>
          <BlockRow className="block-row-checkboxes block-row-one-row">
            <BlockField label={t("lights_motoring_cone")} labelAfter={true} labelFor="LightsMotoringCone" className="block_field_small_22 checkbox-text-bigger">
              <input id="LightsMotoringCone" type="checkbox" checked={remindMotoringCone} onChange={() => { setRemindMotoringCone(!remindMotoringCone); onEdited(); }} />
            </BlockField>
          </BlockRow>
        </>
      )}

      {showInfo && (
        <InfoDialog
          title={t("lights_title")}
          message={<LocalizedMessage message={(m) => m.shipSetting.lights} />}
          onClose={() => setShowInfo(false)}
          style="info"
        />
      )}
    </>
  );
}
